/**
 * Sprint 6 — Channel pairing dialog (WhatsApp + Telegram).
 */
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, X, CheckCircle2, AlertTriangle, Smartphone, Send } from "lucide-react";
import { WhatsAppPairingQR } from "@/components/connections/WhatsAppPairingQR";
import { useChannelPairingPoll } from "@/components/connections/useChannelPairingPoll";
import { connectionPropType, pairingPropType } from "@/lib/propTypes/connections";

function RemoteQrImage({ src, onError }) {
  const imgRef = useRef(null);
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return undefined;
    const handleError = () => onError?.();
    el.addEventListener("error", handleError);
    return () => el.removeEventListener("error", handleError);
  }, [src, onError]);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-center">
      <img
        ref={imgRef}
        src={src}
        alt="WhatsApp pairing QR code"
        className="h-44 w-44 max-w-full object-contain"
        data-testid="channel-pairing-qr"
      />
    </div>
  );
}

RemoteQrImage.propTypes = {
  src: PropTypes.string.isRequired,
  onError: PropTypes.func,
};

function fmtRemaining(ms) {
  if (ms == null || ms < 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function isMockQrUrl(url) {
  if (!url) return false;
  try {
    const host = new URL(url).hostname;
    return host === "mock.openclaw.local" || host.endsWith(".mock.openclaw.local");
  } catch {
    return false;
  }
}

function channelLabelFor(isWhatsApp, isTelegram) {
  if (isWhatsApp) return "WhatsApp";
  if (isTelegram) return "Telegram";
  return "Channel";
}

function channelMeta(connection) {
  const isWhatsApp = connection?.kind === "whatsapp";
  const isTelegram = connection?.kind === "telegram";
  return {
    isWhatsApp,
    isTelegram,
    channelLabel: channelLabelFor(isWhatsApp, isTelegram),
    ChannelIcon: isWhatsApp ? Smartphone : Send,
  };
}

function pairingInstructions({ livePairing, isWhatsApp, isTelegram }) {
  if (livePairing?.instructions) return livePairing.instructions;
  if (isWhatsApp) {
    return "Open WhatsApp on your phone → Settings → Linked Devices → Link a Device → scan this QR code.";
  }
  if (isTelegram) {
    return "In your Telegram app, send the pairing code shown below to @MayaSellixBot to link this workspace.";
  }
  return "Follow the steps in your channel app to complete pairing.";
}

function resolvePairingPanel(pollStatus, panelProps) {
  if (pollStatus === "paired") {
    return (
      <PairingPairedPanel
        channelLabel={panelProps.channelLabel}
        phoneNumber={panelProps.phoneNumber}
        accountName={panelProps.accountName}
      />
    );
  }
  if (pollStatus === "superseded" || pollStatus === "paired_elsewhere") {
    return (
      <PairingSupersededPanel
        message={panelProps.supersededMessage}
        elsewhere={pollStatus === "paired_elsewhere"}
      />
    );
  }
  if (pollStatus === "expired") {
    return <PairingExpiredPanel />;
  }
  return <PairingPendingPanel {...panelProps.pending} />;
}

function PairingSupersededPanel({ message, elsewhere }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center" data-testid="channel-pairing-superseded">
      <AlertTriangle size={20} className="mx-auto text-amber-600" />
      <div className="mt-2 text-[13px] font-semibold text-amber-900">
        {elsewhere ? "Connected in another session" : "Pairing superseded"}
      </div>
      <div className="mt-0.5 text-[11.5px] text-amber-800">
        {message || "Another browser started pairing. Generate a new QR for this browser."}
      </div>
    </div>
  );
}

function PairingPairedPanel({ channelLabel, phoneNumber, accountName }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="channel-pairing-paired">
      <CheckCircle2 size={20} className="mx-auto text-emerald-600" />
      <div className="mt-2 text-[13px] font-semibold text-emerald-900">{channelLabel} connected successfully</div>
      {phoneNumber ? (
        <div className="mt-1 text-[13px] font-mono text-emerald-900" data-testid="channel-pairing-phone">{phoneNumber}</div>
      ) : null}
      {accountName ? <div className="mt-0.5 text-[11.5px] text-emerald-800">{accountName}</div> : null}
    </div>
  );
}

function PairingExpiredPanel() {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center" data-testid="channel-pairing-expired">
      <AlertTriangle size={20} className="mx-auto text-amber-600" />
      <div className="mt-2 text-[13px] font-semibold text-amber-900">Pairing expired — try again</div>
      <div className="mt-0.5 text-[11.5px] text-amber-800">The QR code timed out. Generate a fresh one to continue.</div>
    </div>
  );
}

function PairingPendingPanel({
  loading,
  isWhatsApp,
  livePairing,
  showClientQr,
  showRemoteQr,
  remoteQrUrl,
  qrMissing,
  qrImageError,
  remainingMs,
  pollError,
  onQrImageError,
}) {
  return (
    <div className="space-y-3">
      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center" data-testid="channel-pairing-loading">
          <Loader2 size={24} className="mx-auto animate-spin text-indigo-600" />
          <div className="mt-2 text-[13px] font-medium text-slate-800">Generating pairing QR…</div>
          <div className="mt-1 text-[11px] text-slate-500">Connecting to OpenClaw and starting WhatsApp login.</div>
        </div>
      ) : null}
      {!loading && showClientQr ? (
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-center">
            <WhatsAppPairingQR payload={livePairing.qr_payload} size={176} />
          </div>
          <p className="text-[10.5px] text-slate-500 text-center max-w-xs">
            Keep this window open while you scan. The QR refreshes automatically if WhatsApp rotates it.
          </p>
        </div>
      ) : null}
      {!loading && showRemoteQr ? (
        <RemoteQrImage src={remoteQrUrl} onError={onQrImageError} />
      ) : null}
      {!loading && qrMissing ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-[11.5px] text-amber-900" data-testid="channel-pairing-qr-missing">
          <AlertTriangle size={16} className="mx-auto text-amber-600 mb-1" />
          Couldn&apos;t load the QR code. Try generating a new one.
        </div>
      ) : null}
      {!loading && livePairing?.pairing_code ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Pairing code</div>
          <div className="mt-1 font-mono text-[22px] tracking-[0.18em] font-bold text-slate-900 select-all" data-testid="channel-pairing-code">
            {livePairing.pairing_code}
          </div>
        </div>
      ) : null}
      {!loading ? (
        <div className="flex items-center justify-between text-[11.5px] text-slate-500" data-testid="channel-pairing-status">
          <span className="inline-flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin text-indigo-600" />
            Waiting for scan…
          </span>
          {remainingMs != null ? (
            <span className="font-mono tabular-nums" data-testid="channel-pairing-countdown">
              Expires in {fmtRemaining(remainingMs)}
            </span>
          ) : null}
        </div>
      ) : null}
      {pollError ? (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 px-2.5 py-1.5 text-[11px] flex items-start gap-1.5" data-testid="channel-pairing-poll-error">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          {pollError} — will keep retrying.
        </div>
      ) : null}
    </div>
  );
}

export function ChannelPairingDialog({
  open,
  onOpenChange,
  connection,
  pairing,
  loading = false,
  onPaired,
  onRetry,
}) {
  const {
    pollStatus,
    pollError,
    phoneNumber,
    accountName,
    livePairing,
    qrImageError,
    remainingMs,
    supersededMessage,
    setQrImageError,
  } = useChannelPairingPoll({ open, connection, pairing, onPaired });

  const { isWhatsApp, isTelegram, channelLabel, ChannelIcon } = channelMeta(connection);
  const qrPayload = livePairing?.qr_payload || null;
  const remoteQrUrl = livePairing?.qr_code_url && !isMockQrUrl(livePairing.qr_code_url)
    ? livePairing.qr_code_url
    : null;
  const showClientQr = isWhatsApp && !!qrPayload;
  const showRemoteQr = isWhatsApp && !showClientQr && !!remoteQrUrl && !qrImageError;
  const qrMissing = isWhatsApp && pollStatus === "pending" && !showClientQr && !showRemoteQr;

  return (
    <Dialog open={open} onOpenChange={(v) => onOpenChange(v)}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-md max-h-[90dvh] overflow-y-auto p-4 sm:p-6"
        data-testid="channel-pairing-dialog"
        preventOutsideDismiss
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <ChannelIcon size={15} className="text-indigo-600 shrink-0" />
            Pair {channelLabel}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            {pairingInstructions({ livePairing, isWhatsApp, isTelegram })}
          </DialogDescription>
        </DialogHeader>

        {resolvePairingPanel(pollStatus, {
          channelLabel,
          phoneNumber,
          accountName,
          supersededMessage,
          pending: {
            loading,
            isWhatsApp,
            livePairing,
            showClientQr: showClientQr && pollStatus === "pending",
            showRemoteQr: showRemoteQr && pollStatus === "pending",
            remoteQrUrl,
            qrMissing: qrMissing && pollStatus === "pending",
            qrImageError,
            remainingMs,
            pollError,
            onQrImageError: () => setQrImageError(true),
          },
        })}

        <div className="mt-1 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
          {(pollStatus === "expired" || pollStatus === "superseded" || pollStatus === "paired_elsewhere" || qrMissing || qrImageError) ? (
            <Button size="sm" variant="default" className="h-10 sm:h-8 text-[12px] w-full sm:w-auto" onClick={() => onRetry?.()} data-testid="channel-pairing-retry">
              <RefreshCw size={12} /> {pollStatus === "expired" || pollStatus === "superseded" || pollStatus === "paired_elsewhere" ? "Get new QR" : "Regenerate QR"}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant={pollStatus === "paired" ? "default" : "outline"}
            className="h-10 sm:h-8 text-[12px] w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            data-testid="channel-pairing-cancel"
          >
            {pollStatus === "paired" ? <>Done</> : <><X size={12} /> Cancel</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

PairingSupersededPanel.propTypes = {
  message: PropTypes.string,
  elsewhere: PropTypes.bool,
};

PairingPairedPanel.propTypes = {
  channelLabel: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  accountName: PropTypes.string,
};

PairingPendingPanel.propTypes = {
  loading: PropTypes.bool.isRequired,
  isWhatsApp: PropTypes.bool.isRequired,
  livePairing: pairingPropType,
  showClientQr: PropTypes.bool.isRequired,
  showRemoteQr: PropTypes.bool.isRequired,
  remoteQrUrl: PropTypes.string,
  qrMissing: PropTypes.bool.isRequired,
  qrImageError: PropTypes.bool.isRequired,
  remainingMs: PropTypes.number,
  pollError: PropTypes.string,
  onQrImageError: PropTypes.func.isRequired,
};

ChannelPairingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  connection: connectionPropType,
  pairing: pairingPropType,
  loading: PropTypes.bool,
  onPaired: PropTypes.func,
  onRetry: PropTypes.func,
};

export default ChannelPairingDialog;
